import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Typography,
    Box,
    Divider,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useSelector } from 'react-redux';
import { userSelector } from 'redux/reducers/user';

interface TempEmailModalProps {
    open: boolean;
    onClose: () => void;
    onSubmit: (newEmail: string, newLogin: string, newPassword: string) => Promise<void>;
}

interface BackendErrorResponse {
    message?: string;
    errors?: {
        [key: string]: string[];
    };
}

const UserUpdateModal: React.FC<TempEmailModalProps> = ({ open, onClose, onSubmit }) => {
    const { userData } = useSelector(userSelector);
    const [newEmail, setNewEmail] = useState('');
    const [newLogin, setNewLogin] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [errors, setErrors] = useState({
        email: '',
        login: '',
        password: '',
        confirmPassword: '',
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [submittedEmail, setSubmittedEmail] = useState('');
    const [submittedUsername, setSubmittedUsername] = useState('');

    const validateEmail = (email: string): boolean => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email) && !email.toLowerCase().endsWith('1tul.com');
    };

    const validateLogin = (login: string): boolean => {
        // Username should be at least 3 characters and alphanumeric (can include underscores)
        return login.length >= 3 && /^[a-zA-Z0-9_]+$/.test(login);
    };

    const validatePassword = (password: string): boolean => {
        // Password should be at least 8 characters
        return password.length >= 8;
    };

    const handleBackendError = (error: any) => {
        const newErrors = {
            email: '',
            login: '',
            password: '',
            confirmPassword: '',
        };

        // Check if error response exists and has the expected structure
        if (error?.response?.data) {
            const errorData: BackendErrorResponse = error.response.data;

            // Map backend field names to frontend field names
            const fieldMapping: { [key: string]: keyof typeof newErrors } = {
                email: 'email',
                user_name: 'login',
                password: 'password',
                password_confirmation: 'confirmPassword',
            };

            // Process field-specific errors
            if (errorData.errors) {
                Object.keys(errorData.errors).forEach((backendField) => {
                    const frontendField = fieldMapping[backendField];
                    if (frontendField && errorData.errors![backendField].length > 0) {
                        newErrors[frontendField] = errorData.errors![backendField][0];
                    }
                });
            }

            // If no specific field errors were found, use the general message
            if (!Object.values(newErrors).some((error) => error !== '')) {
                newErrors.email =
                    errorData.message || 'Failed to update profile. Please try again.';
            }
        } else {
            // Generic error fallback
            newErrors.email = 'Failed to update profile. Please try again.';
        }

        setErrors(newErrors);
    };

    const handleSubmit = async () => {
        const newErrors = {
            email: '',
            login: '',
            password: '',
            confirmPassword: '',
        };

        // Validate email
        if (!validateEmail(newEmail)) {
            newErrors.email = 'Please enter a valid email address that is not from 1tul.com domain';
        } else if (newEmail === userData?.email) {
            newErrors.email = 'Please enter a different email address';
        }

        // Validate username
        if (!validateLogin(newLogin)) {
            newErrors.login =
                'Username must be unique, contain at least 3 characters, and contain only letters, numbers, and underscores';
        }

        // Validate password
        if (!validatePassword(newPassword)) {
            newErrors.password = 'Password must be at least 8 characters';
        }

        // Validate password confirmation
        if (newPassword !== confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
        }

        // If there are any errors, set them and return
        if (Object.values(newErrors).some((error) => error !== '')) {
            setErrors(newErrors);
            return;
        }

        setErrors(newErrors);
        setIsSubmitting(true);

        try {
            await onSubmit(newEmail, newLogin, newPassword);

            // Store the submitted values for the success modal
            setSubmittedEmail(newEmail);
            setSubmittedUsername(newLogin);

            // Clear form fields
            setNewEmail('');
            setNewLogin('');
            setNewPassword('');
            setConfirmPassword('');

            // Show success modal instead of closing immediately
            setShowSuccessModal(true);
        } catch (err) {
            handleBackendError(err);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setNewEmail(value);

        // Clear email error when user types
        if (errors.email) {
            setErrors({ ...errors, email: '' });
        }
    };

    const handleLoginChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setNewLogin(value);

        // Clear login error when user types
        if (errors.login) {
            setErrors({ ...errors, login: '' });
        }
    };

    const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setNewPassword(value);

        // Clear password error when user types
        if (errors.password) {
            setErrors({ ...errors, password: '' });
        }

        // Also clear confirm password error if passwords now match
        if (confirmPassword && value === confirmPassword && errors.confirmPassword) {
            setErrors({ ...errors, password: '', confirmPassword: '' });
        }
    };

    const handleConfirmPasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setConfirmPassword(value);

        // Clear confirm password error when user types
        if (errors.confirmPassword) {
            setErrors({ ...errors, confirmPassword: '' });
        }
    };

    const handleSuccessModalClose = () => {
        setShowSuccessModal(false);
        onClose();
    };

    // Button is enabled when all fields have values and not currently submitting
    const isFormValid = newEmail && newLogin && newPassword && confirmPassword && !isSubmitting;

    return (
        <>
            {/* Main Form Modal */}
            <Dialog
                open={open && !showSuccessModal}
                onClose={() => {}} // Prevent closing by clicking outside
                maxWidth="sm"
                fullWidth
                disableEscapeKeyDown // Prevent closing with escape key
            >
                <DialogTitle>
                    <Typography variant="h5" component="h2">
                        Complete Your Profile
                    </Typography>
                </DialogTitle>

                <DialogContent>
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="body1" color="text.secondary" paragraph>
                            Please complete your profile by providing your email, username, and
                            password.
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            autoFocus
                            margin="dense"
                            label="Email Address"
                            type="email"
                            fullWidth
                            variant="outlined"
                            value={newEmail}
                            onChange={handleEmailChange}
                            error={!!errors.email}
                            helperText={errors.email || 'Enter your email address'}
                            disabled={isSubmitting}
                            placeholder="your.email@example.com"
                        />

                        <TextField
                            margin="dense"
                            label="Username"
                            type="text"
                            fullWidth
                            variant="outlined"
                            value={newLogin}
                            onChange={handleLoginChange}
                            error={!!errors.login}
                            helperText={
                                errors.login || 'Choose a username (letters, numbers, underscores)'
                            }
                            disabled={isSubmitting}
                            placeholder="username"
                        />

                        <TextField
                            margin="dense"
                            label="Password"
                            type="password"
                            fullWidth
                            variant="outlined"
                            value={newPassword}
                            onChange={handlePasswordChange}
                            error={!!errors.password}
                            helperText={errors.password || 'At least 8 characters'}
                            disabled={isSubmitting}
                            placeholder="Enter password"
                        />

                        <TextField
                            margin="dense"
                            label="Confirm Password"
                            type="password"
                            fullWidth
                            variant="outlined"
                            value={confirmPassword}
                            onChange={handleConfirmPasswordChange}
                            error={!!errors.confirmPassword}
                            helperText={errors.confirmPassword || 'Re-enter your password'}
                            disabled={isSubmitting}
                            placeholder="Confirm password"
                        />
                    </Box>
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 3 }}>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        color="primary"
                        disabled={!isFormValid}
                        fullWidth
                    >
                        {isSubmitting ? 'Updating...' : 'Complete Profile'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Success Modal */}
            <Dialog
                open={showSuccessModal}
                onClose={handleSuccessModalClose}
                maxWidth="sm"
                fullWidth
            >
                <DialogContent>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            textAlign: 'center',
                            py: 4,
                        }}
                    >
                        <CheckCircleIcon
                            sx={{
                                fontSize: 80,
                                color: 'success.main',
                                mb: 2,
                            }}
                        />
                        <Typography variant="h4" component="h2" sx={{ mb: 3, fontWeight: 500 }}>
                            Success!
                        </Typography>
                        <Typography variant="body1" color="text.primary">
                            Your profile has been updated successfully.
                        </Typography>
                        <Box sx={{ mb: 2, mt: 8 }}>
                            <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                                <strong>Username:</strong> {submittedUsername}
                            </Typography>
                            <Typography variant="body1" color="text.secondary">
                                <strong>Email:</strong> {submittedEmail}
                            </Typography>
                        </Box>
                    </Box>
                </DialogContent>
                <Divider />
                <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'center' }}>
                    <Button
                        onClick={handleSuccessModalClose}
                        variant="contained"
                        color="primary"
                        size="large"
                    >
                        Continue
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default UserUpdateModal;
